import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { matchLabel } from "@/shared/lib/fuzzyMatch";
import { catLabel } from "@/pages/Catalog/engine";

export function NicheChips({
  niches, selected, onToggle, counts, label = "Тематика канала",
}: {
  niches: string[];
  selected: string[];
  onToggle: (id: string) => void;
  counts?: Record<string, number>;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  if (niches.length === 0) return null;

  const q = query.trim();
  const matches = q
    ? niches
        .map((n) => ({ n, score: matchLabel(q, catLabel(n)) }))
        .filter((x): x is { n: string; score: number } => x.score !== null)
        .sort((a, b) => a.score - b.score)
        .map((x) => x.n)
    : niches;

  return (
    <div ref={wrapRef} className="relative flex flex-col gap-1.5">
      <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">{label}</span>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-ink-12 bg-white text-[13px] text-left"
      >
        <span className={cn("truncate", selected.length === 0 && "text-ink-40")}>
          {selected.length === 0 ? "Любая тематика" : selected.map(catLabel).join(", ")}
        </span>
        <ChevronDown size={14} className={cn("text-ink-40 transition-transform shrink-0", open && "rotate-180")} />
      </button>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((n) => (
            <span key={n} className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-pill bg-ink text-white text-[11.5px] font-display font-semibold">
              {catLabel(n)}
              <button type="button" onClick={() => onToggle(n)} className="w-4 h-4 grid place-items-center rounded-full hover:bg-white/20">
                <X size={10} strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-ink-12 rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-ink-12">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск темы — например, «крипта» или «еда»"
              className="w-full h-8 px-2.5 rounded-md border border-ink-12 bg-cream text-[12.5px] outline-none focus:border-ink placeholder:text-ink-40"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {matches.length === 0 && (
              <div className="px-3 py-2.5 text-[12.5px] text-ink-40">Ничего не нашлось</div>
            )}
            {matches.map((n) => {
              const checked = selected.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onToggle(n)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px] hover:bg-ink-6"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={cn(
                      "shrink-0 w-4 h-4 rounded border flex items-center justify-center",
                      checked ? "bg-ink border-ink" : "border-ink-12 bg-white",
                    )}>
                      {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{catLabel(n)}</span>
                  </span>
                  {counts?.[n] !== undefined && <span className="text-[11px] font-mono text-ink-40 shrink-0">{counts[n]}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
